/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import DOMPurify from 'dompurify';
import { FormEntityStatus } from '../../components/submission/constants';
import { SubmissionStatus } from '../../models/SubmissionStatus';

const hasErrorsForKey = (jsonSchemaErrors, key) => {
  return jsonSchemaErrors.filter(error => error.stack?.split(':')[0].includes(key)).length > 0;
};

/**
 * Removes the key values for all the keys that have jsonschema validation errors
 * @param submission
 * @param jsonSchemaErrors
 * @returns submission object with fields set to null for keys with errors.
 */
export const sanitizeSubmission = (submission, jsonSchemaErrors = []) => {
  if (!submission) return null;

  let sanitizedSubmission = {};

  // check if the type of submissionPayload is a string otherwise
  // the Object.keys will transform the value 'string' into an array: {'s', 't', 'r', 'i', 'n', 'g'}
  if (typeof submission !== 'string') {
    Object.keys(submission).forEach((key) => {
      if (hasErrorsForKey(jsonSchemaErrors, key)) {
        sanitizedSubmission[key] = null;
      } else if (typeof submission[key] === 'object' && !Array.isArray(submission[key])) {
        sanitizedSubmission[key] = sanitizeSubmission(submission[key], jsonSchemaErrors);
      } else if (Array.isArray(submission[key])) {
        sanitizedSubmission[key] = [];
        for (const item of submission[key]) {
          sanitizedSubmission[key].push(sanitizeSubmission(item, jsonSchemaErrors));
        }
      } else {
        sanitizedSubmission[key] = submission[key];
      }
    });
  } else {
    // set the string value to the sanitized submission or it will become undefined.
    sanitizedSubmission = submission;
  }

  // One extra layer of sanitization which will remove vulnerabilities for XSS attacks
  let purifiedSubmission = sanitizedSubmission;
  try {
    const result = DOMPurify.sanitize(JSON.stringify(sanitizedSubmission));
    purifiedSubmission = JSON.parse(result);
  } catch (ex) {
    console.log('Cannot sanitize submission.');
  }

  return purifiedSubmission;
};

/**
 * Filters the CONFIRMED items of a form entity array and sorts them by the sequenceNumber.
 * @param items form entity array items
 * @returns {*} form data of the confirmed items
 */
export const getConfirmedEntities = items =>
  items
    .filter(item => item.children?.props?.formData?.state === FormEntityStatus.CONFIRMED)
    .map(item => item.children?.props?.formData)
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber);

/**
 * Filters the items of a form entity array that are opened for editing.
 * (they no not have the state:CONFIRMED field on the form data)
 * @param items form entity array items
 * @returns {*} non confirmed items
 */
export const getNotConfirmedEntities = items =>
  items.filter(
    item =>
      item.children?.props?.formData?.state == null ||
      item.children?.props?.formData?.state === FormEntityStatus.REQUIRE_SERVER_CONFIRMATION,
  );

export const isConfigurationVersionCorrect = (submission, configurationVersion) => {
  return (
    submission?.status === SubmissionStatus.SENT.id ||
    !submission?.configurationVersion ||
    submission?.configurationVersion === configurationVersion?.releaseVersion
  );
};
